import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID, afterNextRender, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { ENVIRONMENT, Environment } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { Channel } from '@usheru-hff/shared/domain';
import { CookieSettingsService } from '@usheru-hff/shared/utils-cookies';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { SdkJob, TrackingEventInterface } from './models/tracking-event';
import { TrackingUsheruService } from './tracking-sdk.service';

// declarations for the global functions made available by the google analytics anf facebook pixel libraries
declare const gtag: (command: string, action: string, properties: unknown) => void;
declare const fbq: (command: string, action: string, properties: unknown) => void;
declare const twq: (command: string, action: string, properties?: unknown) => void;
declare const snaptr: (command: string, action: string, properties?: unknown) => void;
// declare const _paq: (command: string, action: string, properties?: unknown) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const _paq: any;

const TICKETING_ACTIONS: string[] = ['AddToCart', 'InitiateCheckout', 'AddPaymentInfo', 'Purchase'];
const WIDGET_ACTIONS = [
  'SelectCinema',
  'ShowtimesLoaded',
  'StreamingLoaded',
  'CinemaRedirect',
  'AddToCart',
  'OpenMap',
  'SelectDate',
  'SelectTime',
  'SelectCity',
  'StreamingRedirect',
  'ChangeTab'
];

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private readonly gaG4Client!: string;
  private readonly gaG4Usheru!: string;
  private readonly googleAds!: string;

  private readonly hasGoogleAnalytics: boolean;

  private readonly googleTag!: string;
  private readonly facebookPixel!: string;
  private readonly twitter!: string;
  private readonly snapchat!: string;

  private readonly isBrowser: boolean;

  analyticsEnabled = false;
  targetingEnabled = false;

  isGoogleAnalyticsAddedToWebsite = false;
  areWidgetScriptsAdded = false;
  eventsQueue: unknown[] = [];

  private readonly usheruSdk = inject(TrackingUsheruService);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(ENVIRONMENT) private env: Environment,
    private router: Router,
    private channelService: ChannelService,
    private translationService: TranslationsService,
    private locationService: LocationService,
    private cookieSettingsService: CookieSettingsService,
    @Optional() private authService: AuthService
  ) {
    const channel: Channel = this.channelService.channel;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.gaG4Client = channel.googleAnalytics4;
    this.gaG4Usheru = channel?.settings?.googleAnalytics4;
    this.googleAds = channel?.googleAds;
    this.googleTag = channel?.googleTag;
    this.facebookPixel = channel?.facebookPixel;
    this.twitter = channel?.twitter;
    this.snapchat = channel?.snapchat;
    this.hasGoogleAnalytics = !!this.getAnyGoogleAnalyticsAccount() || !!this.googleAds;
    // load tracking scripts
    afterNextRender(() => {
      // No need to continue loading other scripts if is Widget
      if (this.isWidget) {
        return;
      }

      // If it is an application
      this.loadTrackingScripts();
      this.notifyCookiesStatus();
      this.cookieSettingsService.$cookieSettingsChanged.subscribe((/*cookieSettings*/) => {
        this.loadTrackingScripts();
        this.notifyCookiesStatus();
      });
    });
  }

  usheruSdkTrack(job: SdkJob) {
    this.usheruSdk.track(job);
  }

  loadTrackingScripts() {
    const cookieSettings = this.cookieSettingsService.getCookieSettings();
    this.targetingEnabled = cookieSettings.ad_storage.enabled;
    this.analyticsEnabled = cookieSettings.analytics_storage.enabled;

    if (!cookieSettings.accepted) {
      return;
    }

    this.appendGoogleTagToHeadIfExists();

    if (this.analyticsEnabled) {
      this.loadGoogleAnalytics();
      this.trackRouterEvents();
    }
    if (this.targetingEnabled) {
      this.loadFaceBookPixel();
      this.loadTwitter();
      this.loadSnapchat();
    }

    this.trackPreviousEventsIfExist();
  }

  private trackPreviousEventsIfExist() {
    if (this.eventsQueue.length > 0) {
      this.eventsQueue.forEach((e: TrackingEventInterface) => this.trackEvent(e, true));
      this.eventsQueue = [];
    }
  }

  // only for widgets
  private configUsheruTrackingScriptForWidgets() {
    // by addying alias of gtag to window['dataLayer'].push we can use gtag() function to send events to google analytics
    // if google analitics is not added to the site then function gtag() will be undefined. and dataLayer.push wont send pings to google analytics

    let inlineScript = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
    `;
    if (this.gaG4Usheru) {
      // adding groups "usheru only if its a widget"
      inlineScript += `gtag('config', '${this.gaG4Usheru}');`;
    }

    this.appendScriptToHead(inlineScript);
    this.areWidgetScriptsAdded = true;
    this.trackPreviousEventsIfExist();
  }

  private loadGoogleAnalytics(): void {
    if (this.isBrowser && this.hasGoogleAnalytics) {
      const gaAccountId = this.getAnyGoogleAnalyticsAccount();

      const gaScript = this.document.createElement('script');
      gaScript.setAttribute('async', '');
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaAccountId}`;
      this.document.head.appendChild(gaScript);

      // define the global gtag function and invoke it with the google analytics IDs;
      let inlineScript = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
            `;

      // we dont want to add gtag config for a widget client
      if (this.gaG4Client) {
        inlineScript += `gtag('config', '${this.gaG4Client}');`;
      }
      if (this.gaG4Usheru) {
        // adding groups "usheru only if its a widget"
        inlineScript += `gtag('config', '${this.gaG4Usheru}');`;
      }
      if (this.googleAds) {
        inlineScript += `gtag('config', '${this.googleAds}');`;
      }

      this.appendScriptToHead(inlineScript);
      this.isGoogleAnalyticsAddedToWebsite = true;
    }
  }

  private appendGoogleTagToHeadIfExists(): void {
    if (this.googleTag && this.isBrowser) {
      const script = this.document.createElement('script');
      script.setAttribute('async', '');
      script.innerHTML = `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${this.googleTag}');
            `;
      this.document.head.appendChild(script);
    }
  }

  private loadFaceBookPixel(): void {
    if (this.facebookPixel && this.isBrowser) {
      const script = this.document.createElement('script');
      script.setAttribute('async', '');
      script.innerHTML = `
                !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
                n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
                document,'script','https://connect.facebook.net/en_US/fbevents.js');

                fbq('init', '${this.facebookPixel}');
                fbq('track', "PageView");
            `;
      this.document.body.appendChild(script);
    }
  }

  private loadTwitter(): void {
    if (this.twitter && this.isBrowser) {
      const script = this.document.createElement('script');
      script.setAttribute('async', '');
      script.innerHTML = `
                !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
                },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='//static.ads-twitter.com/uwt.js',
                a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');

                twq('init', '${this.twitter}');
                twq('track','PageView');
            `;
      this.document.body.appendChild(script);
    }
  }

  private loadSnapchat(): void {
    if (this.snapchat && this.isBrowser) {
      const script = this.document.createElement('script');
      script.setAttribute('async', '');
      script.innerHTML = `
                (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
                {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
                a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
                r.src=n;var u=t.getElementsByTagName(s)[0];
                u.parentNode.insertBefore(r,u);})(window,document,
                'https://sc-static.net/scevent.min.js');

                snaptr('init', '${this.snapchat}');

                snaptr('track', 'PAGE_VIEW');
            `;
      this.document.body.appendChild(script);
    }
  }

  private trackRouterEvents(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const pageViewEvent = {
          page_title: event.url,
          page_location: event.url,
          page_path: event.urlAfterRedirects,
          country: this.locationService.userCountry?.name || this.locationService.selectedCountry?.name,
          language: this.translationService.language?.englishName
        };

        if (this.hasGoogleAnalytics) {
          gtag('event', 'page_view', pageViewEvent);
        }

        if (this.googleTag) {
          window['dataLayer'].push(pageViewEvent);
        }
      }
    });
  }

  public trackEvent(event: TrackingEventInterface, fromQueue = false): void {
    // redirecting toTrackWidget if is widget
    if (this.isWidget) {
      this.usheruSdk.track({ kind: 'usheru_tag', ...event });
      this.trackWidgetEvent(event);
      return;
    }

    if (this.isBrowser) {
      // adding language and country
      this.addPropertyToEvent(event, 'language', this.translationService.language?.englishName);
      // given priority to real country (this should be used in all deployed environments and selectedCountry on localhost build only)
      const selectedCountry = this.locationService.selectedCountry?.name;
      this.addPropertyToEvent(event, 'country', selectedCountry);

      // the relevant properties for google analytics are called 'event_category' and 'event_label'
      // the below code just recreates the properties object with the updated property names
      // a clone is used so as not to affect the event sent to facebook for which it might make sense to
      // have the properties called 'category' and 'label'
      const properties = event.properties;

      // Adding trackingInitiator = 'usheru-platform' to all our tracking events.
      properties.trackingInitiator = 'usheru-platform';

      const adjustedProperties = this.asGoogleProperties(event.properties);

      if (!fromQueue) this.usheruSdk.track({ kind: 'usheru_tag', ...event });

      // if cookies are not accepted yet save the trackEvents in eventsQueue so that it can be sent when cookies are accepted
      if (!this.isCookiesAccepted) {
        this.eventsQueue.push(event);
        return;
      }

      if (this.hasGoogleAnalytics && this.isTrackingEnabled) {
        if (properties && (properties.category || properties.label)) {
          gtag('event', event.action, adjustedProperties);
        }
      }

      if (this.googleTag) {
        window['dataLayer'].push(adjustedProperties);
      }

      const ticketingAction = TICKETING_ACTIONS.find(val => val.toLowerCase() === event?.action?.toLowerCase());

      if (this.facebookPixel && this.targetingEnabled) {
        if (ticketingAction) {
          fbq('track', event.action, event.properties);
        } else {
          if (event.action === 'FilmPageView') {
            fbq('track', 'ViewContent', event.properties);
          } else if (event.action === 'SubmitSubscribe') {
            fbq('track', 'Lead', event.properties);
          } else if (event.action === 'CinemaRedirect') {
            fbq('track', 'AddToCart', event.properties);
          }
        }
      }

      if (this.twitter && this.targetingEnabled) {
        if (ticketingAction) {
          if (event.action === 'Purchase') {
            twq('track', event.action, this.asTwitterProperties(event.properties));
          } else {
            twq('track', event.action);
          }
        } else if (event.action === 'CinemaRedirect') {
          twq('track', 'AddToCart');
        }
      }

      if (this.snapchat && this.targetingEnabled) {
        if (ticketingAction) {
          const snapchatAction = this.getSnapChatAction(event.action);
          if (snapchatAction === 'PURCHASE') {
            snaptr('track', snapchatAction, this.asSnapChatProperties(event.properties));
          } else {
            snaptr('track', snapchatAction);
          }
        } else if (event.action === 'CinemaRedirect') {
          snaptr('track', 'AddToCart');
        }
      }
    }
  }

  private addPropertyToEvent(event, property, value) {
    event.properties[property] = value;
  }

  private trackWidgetEvent(event) {
    // if cookies are not accepted yet save the trackEvents in eventsQueue so that it can be sent when cookies are accepted

    if (!this.isTrackingEnabled) {
      this.eventsQueue.push(event);
      return;
    }

    // at this point it is possible that scripts  are not configured for tracking so, do so
    if (!this.areWidgetScriptsAdded) {
      this.configUsheruTrackingScriptForWidgets();
    }

    this.addWidgetCategoryPropertyToEvent(event);
    this.sendGlobalEvent(event);
    // .... not needed for now (only used for magnolia pictures)
    // this.sendEventToClientG4Id(event);
  }

  private appendClientIdToProperties(properties): void {
    if (this.isWidget && this.gaG4Client) {
      properties['send_to'] = this.gaG4Client;
    }
  }

  sendGlobalEvent(event) {
    let globalEvent;
    globalEvent = JSON.parse(JSON.stringify(event));
    const globalEventAdjustedProperties = this.asGoogleProperties(globalEvent?.properties);
    if (globalEventAdjustedProperties) {
      gtag('event', globalEvent['action'], globalEventAdjustedProperties);
    }
  }

  sendEventToClientG4Id(event) {
    const clientEvent = JSON.parse(JSON.stringify(event));
    const clientAdjustedProperties = this.asGoogleProperties(clientEvent?.properties);
    this.appendClientIdToProperties(clientAdjustedProperties);
    if (clientAdjustedProperties) {
      gtag('event', clientEvent['action'], clientAdjustedProperties);
    }
  }

  private getAnyGoogleAnalyticsAccount() {
    return this.gaG4Client || this.gaG4Usheru;
  }

  private asGoogleProperties(properties: { [key: string]: string | string[] | number }): {
    [key: string]: string | string[] | number;
  } {
    if (!properties) return null;

    const adjustedProperties = { ...properties };

    if (adjustedProperties.category) {
      adjustedProperties['event_category'] = adjustedProperties.category;
      delete adjustedProperties.category;
    }

    if (adjustedProperties.label) {
      adjustedProperties['event_label'] = adjustedProperties.label;
      delete adjustedProperties.label;
    }

    return adjustedProperties;
  }

  private asTwitterProperties(properties: { [key: string]: string | string[] | number }): {
    [key: string]: string | string[] | number;
  } {
    return {
      value: properties.value,
      currency: properties.currency,
      content_name: properties.label,
      content_ids: properties.idMovie,
      content_type: properties.content_type,
      num_items: properties.num_items,
      order_id: properties.order_id
    };
  }

  private getSnapChatAction(action: string): string {
    switch (action) {
      case 'AddToCart':
        return 'ADD_CART';
      case 'InitiateCheckout':
        return 'START_CHECKOUT';
      case 'AddPaymentInfo':
        return 'ADD_BILLING';
      case 'Purchase':
        return 'PURCHASE';
      default:
        return '';
    }
  }

  private asSnapChatProperties(properties: { [key: string]: string | string[] | number }): {
    [key: string]: string | string[] | number;
  } {
    return {
      price: properties.value,
      currency: properties.currency,
      transaction_id: properties.order_id,
      item_category: properties.category,
      description: properties.label,
      number_items: properties.num_items
    };
  }

  appendScriptToHead(script: string) {
    const scriptElement = this.document.createElement('script');
    scriptElement.innerHTML = script;
    this.document.head.appendChild(scriptElement);
  }

  private addWidgetCategoryPropertyToEvent(event): void {
    if (this.isWidget && WIDGET_ACTIONS.includes(event.action)) {
      // we can define what properties to add to widget event here
      event.properties['category'] = 'usheru-where-to-watch';
    }
  }

  private notifyCookiesStatus() {
    if (typeof window['dataLayer'] == 'undefined') {
      return;
    }
    const cookieSettings = this.cookieSettingsService.getCookieSettings();
    const cookiesSettingsEvent = {
      event: 'cookie_consent_update',
      analytics_storage: cookieSettings.analytics_storage?.enabled ? 'granted' : 'denied',
      ad_storage: cookieSettings.ad_storage?.enabled ? 'granted' : 'denied',
      functionality_storage: cookieSettings.functionality_storage.enabled ? 'granted' : 'denied',
      security_storage: cookieSettings.security_storage.enabled ? 'granted' : 'denied'
    };
    window['dataLayer'].push(cookiesSettingsEvent);
  }

  get isWidget() {
    return this.env.appType === 'widget';
  }

  get isWidgetTrackingEnabled() {
    // check if site has google_tag_data.entries.analytics_storage
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window['google_tag_data'] as any)?.ics?.entries?.analytics_storage) {
      return this.hasGTagsAnaliticsConsent();
    }
    // if google_tag_data and dataLayer is there means there is google tag added to the site
    if (window['google_tag_data']) return true;
    return false;
  }

  get isCookiesAccepted() {
    if (this.isWidget) {
      return this.isWidgetTrackingEnabled;
    } else {
      return this.cookieSettingsService.getCookieSettings()?.accepted;
    }
  }

  get isTrackingEnabled() {
    if (this.isWidget) {
      return this.isWidgetTrackingEnabled;
    } else {
      return this.analyticsEnabled;
    }
  }
  /**
   * It will check if the user has accepted the analytics consent only if google_tag_data is present in site. (More useful for widgets)
   * @returns  boolean
   */
  hasGTagsAnaliticsConsent(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entries = (window['google_tag_data'] as any)?.ics?.entries;
    // read the analytics_storage value from the consent mode data in this order update/default/true
    if (entries?.analytics_storage?.update !== undefined) {
      return entries?.analytics_storage?.update;
    }
    if (entries?.analytics_storage?.default !== undefined) {
      return entries?.analytics_storage?.default;
    }
    return true;
  }
}
